import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as _ from 'lodash';
import {
  type OperatorFunction,
  ReplaySubject,
  Subject,
  Subscription,
  debounceTime,
  filter,
  map,
} from 'rxjs';
import * as SpaceTaskGet from '../../server/routes/spaces/tasks/get';
import * as TaskDelete from '../../server/routes/tasks/delete';
import * as TaskGet from '../../server/routes/tasks/get';
import * as TaskPatch from '../../server/routes/tasks/patch';
import * as TaskPost from '../../server/routes/tasks/post';
import * as TaskPostBatch from '../../server/routes/tasks/post-batch';
import {
  generateIdFromOrgId,
  generatePath,
  sortByTitleExactFirst,
  zeroId,
} from '../../server/services/utils.service';
import type { FileElement } from '../../server/types/file-types';
import type { OrgTag } from '../../server/types/org-types';
import {
  type OrgTask,
  type OrgTaskFrontEnd,
  type OrgTaskUpdates,
  type Schedule,
  incrementalTaskWeight,
  placeMidWeight,
} from '../../server/types/task-types';
import type { DeepReadonly } from '../../server/util-type';
import { AssignedToDialogComponent } from './assigned-to-dialog/assigned-to-dialog.component';
import { AuthUserService } from './auth-user.service';
import { ChangeDateDialogComponent } from './change-date-dialog/change-date-dialog.component';
import { DeleteTaskDialogComponent } from './delete-task-dialog/delete-task-dialog.component';
import { ImportTasksComponent } from './import-tasks/import-tasks.component';
import { TagDialogComponent } from './tag-dialog/tag-dialog.component';
import { TagService } from './tag.service';
import { TaskScheduleDialogComponent } from './task-schedule-dialog/task-schedule-dialog.component';
import { TrashTasksDialogComponent } from './trash-tasks-dialog/trash-tasks-dialog.component';
import { UtilsService } from './utils.service';
import { WebSocketService } from './web-socket.service';

export interface OrgTaskWithUpdates extends OrgTaskFrontEnd {
  serverTask: OrgTaskFrontEnd & { salt?: string };
  updates?: OrgTaskUpdates;
  autoDeletable?: true;
  subscriptions: {
    saveTitleDebounce?: Subject<true>;
    saveTitleDebounceSub?: Subscription;
    saveDescriptionDebounce?: Subject<true>;
    saveDescriptionDebounceSub?: Subscription;
  };
}

export const listStageArray = [
  'backlog',
  'current',
  'completed',
  'trash',
] as const;
export type ListStage = (typeof listStageArray)[number];
export type CompletedAtRange = {
  start: number; // inclusive
  end?: number; // exclusive
};

export type CompletedListFilter = {
  listStage: 'completed';
  completedAtRange: CompletedAtRange; // only for listStage='completed'
  assignedTo?: string | undefined | null; // null is unassigned
  tagIds: string[] | null; // null means no tags
};

export type ListFilter = {
  assignedTo?: string | undefined | null; // null is unassigned
  tagIds: string[] | null; // null means no tags
} & (
  | {
      listStage: 'backlog' | 'current' | 'trash' | 'completed';
      completedAtRange?: undefined; // only for listStage='completed'
      recentlyCompletedBuffer?: number;
    }
  | CompletedListFilter
);

type SpaceTaskData = {
  orgId: string;
  spaceId: string;
  taskMap: Map<string, OrgTaskWithUpdates>;
  parentTaskMap: Map<string, OrgTaskWithUpdates[]>;
  taskCount?: number; // used to determine if some of the tasks have been deleted from the cache
  requestTime?: number;
  backlogReady: ReplaySubject<boolean>;
  serverBacklogTasks: { _id: string; requestStart?: number }[];
};

export type TaskItem = {
  taskId: string;
  task: DeepReadonly<OrgTaskWithUpdates>;
};

export type TaskExportDownload = {
  type: 'fociva-export v0.0.2';
  orgId: string;
  spaceId: string;
  users: { _id: string; name: string }[];
  tasks: OrgTaskFrontEnd[];
  tags: OrgTag[];
};

export type ImportBulkTask = {
  _id: string;
  spaceId: string;
  salt: string;
  tagIds: string[];
  assignedTo?: string;
  weight: number;
  parentId?: string;
  parentWeight?: number;
  timeEstimate?: number;
  schedule?: Schedule;
  dueAt?: number;
  currentAt?: number;
  completedAt?: number;
  title: string;
  description: string;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  localAt: number;
  trashedAt?: number;
  deletedAt?: number;
  files: FileElement[];
};

type SelectionData = {
  orgId: string;
  spaceId: string;
  taskId: string;
  listId: string;
  parentId: string | undefined;
  orphaned: boolean;
};

export type ChangeDateDialogComponentData = {
  orgId: string;
  spaceId: string;
  taskId: string;
  type: 'currentAt' | 'completedAt';
};

export type TaskScheduleDialogComponentData = {
  orgId: string;
  spaceId: string;
  taskId: string;
};

export type SearchResult = {
  orgId: string;
  spaceId: string;
  taskId: string;
  title: string;
  completed: boolean;
  weight: number; // 0 tp 1
} & (
  | {
      inCurrentContext: true;
      listId: string;
      parentPathTaskIds: string[];
    }
  | {
      inCurrentContext: false;
      listId?: undefined;
      parentPathTaskIds?: undefined;
    }
);
@Injectable({ providedIn: 'root' })
export class TaskService {
  private http = inject(HttpClient);
  private snackBar = inject(MatSnackBar);
  private authUserService = inject(AuthUserService);
  private dialog = inject(MatDialog);
  private utilsService = inject(UtilsService);
  private tagService = inject(TagService);
  private webSocketService = inject(WebSocketService);

  private taskUpdatesSubject = new Subject<OrgTaskWithUpdates[]>();
  private taskRemoveSubject = new Subject<{
    orgId: string;
    spaceId: string;
    taskId: string;
  }>();

  private spaceData: SpaceTaskData[] = [];

  private taskCursorSubject = new ReplaySubject<{
    taskId: string;
    listId: string;
    start: number;
    end: number;
  }>(1, 100); // reset after a 10th of a second so that it isn't constantly trying to select the task

  private selectionStart: SelectionData | undefined = undefined;
  private selectionEnd: SelectionData | undefined = undefined;
  selections: SelectionData[] = [];
  selectionsSubject = new Subject<SelectionData[]>();
  private selectSubject = new Subject<{
    listId: string;
    taskId: string;
    parentId: string | undefined;
    orphaned: boolean;
  }>();
  private selectDisplaySubject = new Subject<
    SelectionData | { listId: string; taskId: undefined }
  >();
  private deselectSubject = new Subject<{
    listId: string;
    taskId: string;
    parentId: string | undefined;
    orphaned: boolean;
  }>();
  private blurSubject = new Subject<{
    listId: string;
    taskId: string;
  }>();
  private tabInSelectionSubject = new Subject<{
    listId: string;
    taskId: string;
  }>();
  // Sends a message to the parent task of the selected tasks to get its info
  private tabOutSelectionSubject = new Subject<{
    listId: string;
    taskId: string;
  }>();
  private arrowSubject = new Subject<{
    listId: string;
    taskId: string;
    key: 'ArrowUp' | 'ArrowDown';
    shiftKey: boolean;
  }>();
  private subListExpandSubject = new Subject<{
    listId: string;
    taskId: string;
    action: 'expand' | 'collapse';
  }>();
  private launchDialogSubject = new Subject<{
    listId: string;
    taskId: string;
    dialog: 'assignedTo' | 'tag';
  }>();
  private taskScrollSubject = new ReplaySubject<
    | {
        orgId: string;
        taskId: string;
        parentPathTaskIds: string[];
      }
    | undefined
  >(1);

  constructor() {
    addEventListener('keydown', (event: KeyboardEvent) => {
      const bodyElement = document.getElementsByTagName('body')[0];
      if (bodyElement !== event.target) {
        return; // this is to only intercept keyboard events if not focused on some other elements
      }

      if (event.key === 'Tab') this.handleTabEvent(event);

      if (
        !this.utilsService.ctrlOrCmd(event) &&
        (event.key === 'ArrowUp' || event.key === 'ArrowDown')
      ) {
        this.handleUpOrDownEvent(event);
      }

      if (this.utilsService.ctrlOrCmd(event) && event.key === 'ArrowDown')
        this.expandSelectedSubtasks('expand');

      if (this.utilsService.ctrlOrCmd(event) && event.key === 'ArrowUp')
        this.expandSelectedSubtasks('collapse');

      if (event.key === 'Escape') {
        const originalSelections = this.selections;
        this.selections = [];
        this.selectionsSubject.next(this.selections);
        this.selectionStart = undefined;
        this.selectionEnd = undefined;
        originalSelections.forEach((s) => this.deselectSubject.next(s));
      }

      if (event.key === 'Enter' && this.utilsService.ctrlOrCmd(event))
        this.toggleSelectedComplete();

      if (event.key === 'Backspace' || event.key === 'Delete')
        this.trashSelectedTasks();

      if (event.key === '@') this.launchAssignedTo();
      if (event.key === '#') this.launchTag();
    });

    this.startWebSocket();
  }

  startWebSocket(): void {
    this.webSocketService.pushObservable.subscribe((socketResult) => {
      if (socketResult.service === 'task') {
        const { orgId } = socketResult;

        if (socketResult.action === 'update') {
          const { tasks: ts } = socketResult.data;

          _.uniq(ts.map(({ _id }) => _id)).forEach((_id) => {
            const tasks = ts.filter((t) => t._id === _id);
            if (!tasks.length) return;
            this.storeResponseData({ orgId, spaceId: tasks[0].spaceId, tasks });
          });
        }
      }
    });
  }

  handleTabEvent(event: KeyboardEvent) {
    const canTab = this.selectionValidateCanTab();
    if (!canTab) {
      return;
    }
    const { parentId } = canTab;
    if (event.shiftKey) {
      if (!parentId) return; // no parentId, cannot tab out
      event.preventDefault();
      this.tabOutSelectionSubject.next({
        listId: canTab.listId,
        taskId: parentId,
      });
    } else {
      const tasks: DeepReadonly<OrgTaskWithUpdates>[] = [];
      const tasksTemp = parentId
        ? this.getParentWeightSortedSelectedTasks()
        : this.getWeightSortedSelectedTasks();
      tasksTemp.forEach((t) => {
        if (t) tasks.push(t);
      });
      event.preventDefault();
      this.tabInSelectionSubject.next({
        listId: canTab.listId,
        taskId: tasks[0]._id,
      });
    }
  }

  handleUpOrDownEvent(event: KeyboardEvent) {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')
      throw new Error('Unexpected state');
    const selection =
      this.selectionEnd ?? this.selectionStart ?? this.selections[0];
    if (!selection) return;
    const key = event.key;
    const shiftKey = event.shiftKey;
    this.arrowSubject.next({
      listId: selection.listId,
      taskId: selection.taskId,
      key,
      shiftKey,
    });
  }

  expandSelectedSubtasks(action: 'expand' | 'collapse') {
    this.selections.forEach(({ listId, taskId }) =>
      this.subListExpandSubject.next({
        listId,
        taskId,
        action,
      })
    );
  }

  expandTask({ listId, taskId }: { listId: string; taskId: string }) {
    this.subListExpandSubject.next({
      listId,
      taskId,
      action: 'expand',
    });
  }

  toggleSelectedComplete() {
    if (this.selections.length === 0) return;

    const tasks: DeepReadonly<OrgTaskWithUpdates>[] = [];

    this.selections.forEach((s) => {
      const task = this.getTaskLocalSync(s);
      if (task) tasks.push(task);
      console.error('taskId:', s.taskId, 'not found in toggleSelectedComplete');
    });
    // If all completed, then we want to uncomplete. Otherwise, we should
    // complete the tasks that are not completed.
    const setComplete = tasks.find((t) => !t.completedAt);
    const time = Date.now();
    tasks.forEach((t) => {
      if (setComplete) {
        if (!t.completedAt)
          this.updateTask(
            {
              orgId: t.orgId,
              spaceId: t.spaceId,
              taskId: t._id,
            },
            { completedAt: time }
          );
      } else {
        if (t.completedAt)
          this.updateTask(
            {
              orgId: t.orgId,
              spaceId: t.spaceId,
              taskId: t._id,
            },
            { completedAt: null }
          );
      }
    });
  }

  trashSelectedTasks() {
    if (this.selections.length === 0) return;

    const dialogConfig = new MatDialogConfig();

    const tasks: DeepReadonly<OrgTaskWithUpdates>[] = [];
    this.selections.forEach((s) => {
      const task = this.getTaskLocalSync(s);
      if (task) tasks.push(task);
    });
    dialogConfig.data = { tasks };
    const dialogRef = this.dialog.open(TrashTasksDialogComponent, dialogConfig);
    let dialogSubscription: Subscription | undefined = undefined;
    dialogSubscription = dialogRef.afterClosed().subscribe({
      next: (res) => {
        if (!res) return;
        const time = Date.now();
        tasks.forEach((t) => {
          if (t.trashedAt) return;
          this.updateTask(
            {
              orgId: t.orgId,
              spaceId: t.spaceId,
              taskId: t._id,
            },
            { trashedAt: time }
          );
        });
        dialogSubscription?.unsubscribe();
      },
      error: (err) => {
        console.error(err);
        dialogSubscription?.unsubscribe();
      },
    });
  }

  launchAssignedTo() {
    // Don't launch
    if (this.selections.length === 0) return;
    // Launch on the task itself
    if (this.selections.length === 1)
      return this.launchDialogSubject.next({
        listId: this.selections[0].listId,
        taskId: this.selections[0].taskId,
        dialog: 'assignedTo',
      });
    // Launch bulk dialog
    const tasks: DeepReadonly<OrgTaskWithUpdates>[] = [];
    this.selections.forEach((s) => {
      const task = this.getTaskLocalSync(s);
      if (task) tasks.push(task);
    });
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = { tasks };
    this.dialog.open(AssignedToDialogComponent, dialogConfig);
  }

  launchTag() {
    // Don't launch
    if (this.selections.length === 0) return;
    // Launch on the task itself
    if (this.selections.length === 1)
      return this.launchDialogSubject.next({
        listId: this.selections[0].listId,
        taskId: this.selections[0].taskId,
        dialog: 'tag',
      });
    // Launch bulk dialog
    const tasks: DeepReadonly<OrgTaskWithUpdates>[] = [];
    this.selections.forEach((s) => {
      const task = this.getTaskLocalSync(s);
      if (task) tasks.push(task);
    });
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = { tasks };
    this.dialog.open(TagDialogComponent, dialogConfig);
  }

  getSpaceDataForStoring({
    orgId,
    spaceId,
  }: {
    orgId: string;
    spaceId: string;
  }) {
    let spaceData: SpaceTaskData | undefined = this.spaceData.find(
      (t) => t.spaceId === spaceId
    );
    if (!spaceData) {
      spaceData = {
        orgId,
        spaceId,
        taskMap: new Map<string, OrgTaskWithUpdates>(),
        parentTaskMap: new Map<string, OrgTaskWithUpdates[]>(),
        backlogReady: new ReplaySubject<boolean>(1),
        serverBacklogTasks: [],
      };
      this.spaceData.push(spaceData);
    }
    return spaceData;
  }

  refreshTaskData({ orgId, spaceId }: { orgId: string; spaceId: string }) {
    const spaceData = this.getSpaceDataForStoring({ orgId, spaceId });

    let path = generatePath<{ orgId: string; spaceId: string }>(
      '/api/orgs/:orgId/spaces/:spaceId/tasks',
      { orgId, spaceId }
    );

    if (spaceData.requestTime) {
      path += '?after=' + spaceData.requestTime;
    }

    this.http
      .get<SpaceTaskGet.ResBody>(path)
      .subscribe(({ tasks, requestTime }) => {
        this.storeResponseData({ orgId, spaceId, tasks, requestTime });
      });
  }

  storeResponseData({
    orgId,
    spaceId,
    tasks: tasksResult,
    requestTime,
  }: {
    orgId: string;
    spaceId: string;
    tasks?: OrgTask[];
    requestTime?: number;
  }) {
    const spaceData = this.getSpaceDataForStoring({ orgId, spaceId });

    const tasks: OrgTaskWithUpdates[] = [];
    tasksResult?.forEach((task) => {
      const stored = this.storeServerTaskData({ task, updateSubject: false });
      if (stored) tasks.push(stored);
    });

    if (
      requestTime &&
      (!spaceData.requestTime || requestTime > spaceData.requestTime)
    )
      spaceData.requestTime = requestTime;
    if (tasks?.length) this.taskUpdatesSubject.next(tasks);
  }

  storeServerTaskData({
    task: serverTask,
    updateSubject = true,
  }: {
    task: OrgTask;
    updateSubject?: boolean; // false to update multiple tasks simultaneously not in this function
  }): OrgTaskWithUpdates | undefined {
    const { orgId, spaceId, _id: taskId } = serverTask;

    // Try to store the server task
    const spaceData = this.getSpaceDataForStoring({ orgId, spaceId });
    const oldTask: OrgTaskWithUpdates | undefined =
      spaceData.taskMap.get(taskId);

    let task: OrgTaskWithUpdates;
    if (!oldTask) {
      // Put in data for the first time
      task = {
        ..._.cloneDeep(serverTask),
        serverTask: _.cloneDeep(serverTask),
        subscriptions: {},
      };
      spaceData.taskMap.set(taskId, task);
    } else if (serverTask.updatedAt > oldTask.serverTask.updatedAt) {
      // If there is an existing task get updates, check if any of them are no
      // longer useful, push remaining back in.
      const existingUpdates = oldTask.updates;
      const updates: OrgTaskUpdates = {};
      if (existingUpdates) {
        // assignedTo
        if (
          typeof existingUpdates.assignedTo === 'string' &&
          existingUpdates.assignedTo !== serverTask.assignedTo
        )
          updates.assignedTo = existingUpdates.assignedTo;
        if (existingUpdates.assignedTo === null && serverTask.assignedTo)
          updates.assignedTo = null;

        // weight
        if (
          typeof existingUpdates.weight === 'number' &&
          existingUpdates.weight !== serverTask.weight
        )
          updates.weight = existingUpdates.weight;

        // parentId
        if (
          typeof existingUpdates.parentId === 'string' &&
          existingUpdates.parentId !== serverTask.parentId
        )
          updates.parentId = existingUpdates.parentId;
        if (existingUpdates.parentId === null && serverTask.parentId)
          updates.parentId = null;

        // parentWeight
        if (
          typeof existingUpdates.parentWeight === 'number' &&
          existingUpdates.parentWeight !== serverTask.parentWeight
        )
          updates.parentWeight = existingUpdates.parentWeight;

        // timeEstimate
        if (
          typeof existingUpdates.timeEstimate === 'number' &&
          existingUpdates.timeEstimate !== serverTask.timeEstimate
        )
          updates.timeEstimate = existingUpdates.timeEstimate;
        if (existingUpdates.timeEstimate === null && serverTask.timeEstimate)
          updates.timeEstimate = null;

        // schedule
        if (
          existingUpdates.schedule &&
          serverTask.schedule &&
          !_.isEqual(existingUpdates.schedule, serverTask.schedule)
        )
          updates.schedule = existingUpdates.schedule;
        if (existingUpdates.schedule === null && serverTask.schedule)
          updates.schedule = null;

        // dueAt
        if (
          typeof existingUpdates.dueAt === 'number' &&
          existingUpdates.dueAt !== serverTask.dueAt
        )
          updates.dueAt = existingUpdates.dueAt;
        if (existingUpdates.dueAt === null && serverTask.dueAt)
          updates.dueAt = null;

        // currentAt
        if (
          typeof existingUpdates.currentAt === 'number' &&
          existingUpdates.currentAt !== serverTask.currentAt
        )
          updates.currentAt = existingUpdates.currentAt;
        if (existingUpdates.currentAt === null && serverTask.currentAt)
          updates.currentAt = null;

        // completedAt
        if (
          typeof existingUpdates.completedAt === 'number' &&
          existingUpdates.completedAt !== serverTask.completedAt
        )
          updates.completedAt = existingUpdates.completedAt;
        if (existingUpdates.completedAt === null && serverTask.completedAt)
          updates.completedAt = null;

        // title
        if (
          typeof existingUpdates.title === 'string' &&
          existingUpdates.title !== serverTask.title
        )
          updates.title = existingUpdates.title;

        // description
        if (
          typeof existingUpdates.description === 'string' &&
          existingUpdates.description !== serverTask.description
        )
          updates.description = existingUpdates.description;

        // tagIds - push
        const pushTagIds = existingUpdates.push?.tagIds?.filter(
          (t) => !serverTask.tagIds.includes(t)
        );
        if (pushTagIds?.length) updates.push = { tagIds: pushTagIds };

        // tagIds - pull
        const pullTagIds = existingUpdates.pull?.tagIds?.filter((t) =>
          serverTask.tagIds.includes(t)
        );
        if (pullTagIds?.length) updates.pull = { tagIds: pullTagIds };

        // fileIds - pull
        const pullFileIds = existingUpdates.pull?.fileIds?.filter((fileId) =>
          serverTask.files.find((f) => f._id === fileId)
        );
        if (pullFileIds?.length) {
          updates.pull = updates.pull
            ? { ...updates.pull, fileIds: pullFileIds }
            : { fileIds: pullFileIds };
        }

        // userIds - push
        const pushUserIds = existingUpdates.push?.userIds?.filter(
          (u) => !serverTask.userIds.includes(u)
        );
        if (pushUserIds?.length) updates.push = { userIds: pushUserIds };
      }

      const baseTask = applyUpdatesToTask({ serverTask, updates });
      task = {
        ...baseTask,
        serverTask: _.cloneDeep(serverTask),
        updates: _.cloneDeep(updates),
        subscriptions: {},
      };

      if (Object.keys(updates).length > 0) {
        this.addUpdateToBacklog(task);
      } else {
        this.removeUpdateFromBacklog(task);
      }

      // Replace the task in the array
      spaceData.taskMap.set(taskId, task);
    } else {
      return; // no update to this.task or this.parentTaskMap
    }

    this.storeParentTaskMap({ newTask: task, oldTask });

    if (updateSubject) this.taskUpdatesSubject.next([task]);
    return task;
  }

  storeTaskMap(task: OrgTaskWithUpdates) {
    this.getSpaceDataForStoring({
      orgId: task.orgId,
      spaceId: task.spaceId,
    }).taskMap.set(task._id, task);
  }

  storeParentTaskMap({
    newTask,
    oldTask,
  }: {
    newTask: OrgTaskWithUpdates;
    oldTask?: OrgTaskWithUpdates;
  }) {
    const parentTaskMap = this.getSpaceDataForStoring({
      orgId: newTask.orgId,
      spaceId: newTask.spaceId,
    }).parentTaskMap;

    // Update parent store
    // Clean up the old parent reference
    if (oldTask?.parentId && oldTask.parentId !== newTask.parentId) {
      const children = parentTaskMap.get(oldTask.parentId);
      if (!children) {
        console.error(
          'expected parentId to have existing children, but none found'
        );
      } else {
        const childIndex = children.findIndex((c) => c._id === newTask._id);
        if (childIndex !== -1) {
          children.splice(childIndex, 1);
        } else {
          console.error('expected child to be in parentTaskMap');
        }
      }
    }

    // Set the new parent
    if (newTask.parentId) {
      const children = parentTaskMap.get(newTask.parentId);
      if (!children) {
        parentTaskMap.set(newTask.parentId, [newTask]);
      } else {
        const childIndex = children.findIndex((c) => c._id === newTask._id);
        if (childIndex === -1) {
          children.push(newTask);
        } else {
          if (children[childIndex].updatedAt < newTask.updatedAt) {
            children.splice(childIndex, 1, newTask);
          }
        }
      }
    }
  }

  updateTask(
    {
      orgId,
      spaceId: inputSpaceId,
      taskId,
    }: { orgId: string; spaceId?: string; taskId: string },
    inputUpdates: OrgTaskUpdates,
    options?: {
      applyUpdateToChildren?: boolean;
      preventChildPropagation?: boolean;
    }
  ) {
    const { applyUpdateToChildren = true, preventChildPropagation = false } =
      options ?? {};

    const existingTask = this.getTaskLocalSync({
      orgId,
      spaceId: inputSpaceId,
      taskId,
    });

    if (!existingTask) throw new Error('taskId not found in stored tasks');
    const spaceId = inputSpaceId ?? existingTask.spaceId;
    const spaceData = this.getSpaceDataForStoring({ orgId, spaceId });

    const isNewTask = existingTask.createdAt === 0; // #isNewTask when createdAt === 0

    const serverTask = _.cloneDeep(existingTask.serverTask);

    const updates = _.cloneDeep(inputUpdates);
    for (const key in updates)
      if (updates[key] === undefined) delete updates[key];

    const existingUpdates = _.cloneDeep(existingTask.updates);
    for (const key in existingUpdates)
      if (existingUpdates[key] === undefined) delete existingUpdates[key];

    const mergedUpdates: OrgTaskUpdates = {
      ..._.cloneDeep(existingUpdates), // Bring in any waiting updates
      ..._.cloneDeep(updates), // Overwrite with new updates
      pull: undefined,
      push: undefined,
    };

    const tempPushTagIds1 = _.uniq([
      ...(updates.push?.tagIds ?? []),
      ...(existingUpdates?.push?.tagIds ?? []),
    ]);
    const tempPullTagIds1 = _.uniq([
      ...(updates.pull?.tagIds ?? []),
      ...(existingUpdates?.pull?.tagIds ?? []),
    ]);
    // Check for undos
    const tempPushTagIds2 = tempPushTagIds1.filter(
      (t1) => !tempPullTagIds1.find((t2) => t2 === t1)
    );
    const tempPullTagIds2 = tempPullTagIds1.filter(
      (t1) => !tempPushTagIds1.find((t2) => t2 === t1)
    );
    // Don't add already added tags, do remove already removed tags
    const pushTagIds = tempPushTagIds2.filter(
      (t1) => !serverTask.tagIds.find((t2) => t2 === t1)
    );
    const pullTagIds = tempPullTagIds2.filter((t1) =>
      serverTask.tagIds.find((t2) => t2 === t1)
    );

    // Remove files
    const tempPullFileIds1 = _.uniq([
      ...(updates.pull?.fileIds ?? []),
      ...(existingUpdates?.pull?.fileIds ?? []),
    ]);
    const pullFileIds = tempPullFileIds1.filter((t1) =>
      serverTask.files.find((t2) => t2._id === t1)
    );

    const tempPushUserIds1 = _.uniq([
      ...(updates.push?.userIds ?? []),
      ...(existingUpdates?.push?.userIds ?? []),
    ]);
    // Don't add user again
    const pushUserIds = tempPushUserIds1.filter(
      (u) => !serverTask.userIds.includes(u)
    );

    if (mergedUpdates.title === serverTask.title) delete mergedUpdates.title;
    if (mergedUpdates.description === serverTask.description)
      delete mergedUpdates.description;

    // Remove unnecessary nulls
    const nullableKeys: (
      | 'assignedTo'
      | 'parentId'
      | 'timeEstimate'
      | 'schedule'
      | 'dueAt'
      | 'currentAt'
      | 'completedAt'
      | 'trashedAt'
    )[] = [
      'assignedTo',
      'parentId',
      'timeEstimate',
      'schedule',
      'dueAt',
      'currentAt',
      'completedAt',
      'trashedAt',
    ];
    nullableKeys.forEach((n) => {
      if (mergedUpdates[n] === null && !serverTask[n]) delete mergedUpdates[n];
    });

    if (pushTagIds.length || pushUserIds.length) {
      mergedUpdates.push = {};
      if (pushTagIds.length)
        mergedUpdates.push.tagIds = _.cloneDeep(pushTagIds);
      if (pushUserIds.length)
        mergedUpdates.push.userIds = _.cloneDeep(pushUserIds);
    }
    if (pullTagIds.length || pullFileIds.length) {
      mergedUpdates.pull = {};
      if (pullTagIds.length)
        mergedUpdates.pull.tagIds = _.cloneDeep(pullTagIds);
      if (pullFileIds.length)
        mergedUpdates.pull.fileIds = _.cloneDeep(pullFileIds);
    }

    // Clean updates
    if (mergedUpdates.push?.tagIds?.length === 0)
      mergedUpdates.push.tagIds = undefined;
    if (mergedUpdates.push?.userIds?.length === 0)
      mergedUpdates.push.userIds = undefined;
    if (
      !mergedUpdates.push ||
      (!mergedUpdates.push?.tagIds && !mergedUpdates.push?.userIds)
    )
      delete mergedUpdates.push;

    if (mergedUpdates.pull?.tagIds?.length === 0)
      mergedUpdates.pull.tagIds = undefined;
    if (mergedUpdates.pull?.fileIds?.length === 0)
      mergedUpdates.pull.fileIds = undefined;
    if (
      !mergedUpdates.pull ||
      (!mergedUpdates.pull?.tagIds && !mergedUpdates.pull?.fileIds)
    )
      delete mergedUpdates.pull;

    // Don't delete title if we are trashing task
    if (mergedUpdates.trashedAt && mergedUpdates.title === '')
      mergedUpdates.title = undefined;

    const baseTask = applyUpdatesToTask({
      serverTask,
      updates: mergedUpdates,
    });
    const { autoDeletable } = existingTask;
    const updatedTask: OrgTaskWithUpdates = {
      ..._.cloneDeep(baseTask),
      serverTask: _.cloneDeep(serverTask),
      autoDeletable,
      subscriptions: existingTask.subscriptions,
    };

    if (Object.keys(mergedUpdates).length !== 0)
      updatedTask.updates = mergedUpdates;

    const oldTask = existingTask;

    if (
      _.isEqual(
        JSON.parse(JSON.stringify({ ...oldTask, subscriptions: undefined })),
        JSON.parse(JSON.stringify({ ...updatedTask, subscriptions: undefined }))
      ) &&
      !updatedTask.updates
    )
      return;

    // Update front end data
    this.storeParentTaskMap({ newTask: updatedTask, oldTask });
    spaceData.taskMap.set(taskId, updatedTask);
    this.taskUpdatesSubject.next([updatedTask]);

    // Send updates to children
    if (
      applyUpdateToChildren &&
      !preventChildPropagation && // used to prevent infinite loops
      (mergedUpdates.assignedTo !== undefined ||
        mergedUpdates.completedAt !== undefined ||
        mergedUpdates.trashedAt !== undefined ||
        // mergedUpdates.cancelledAt !== undefined || // TODO: cancelledAt
        mergedUpdates.currentAt !== undefined ||
        mergedUpdates.push?.tagIds !== undefined ||
        mergedUpdates.pull?.tagIds !== undefined)
    ) {
      const children = this.getChildrenMutable({
        orgId,
        spaceId,
        parentId: taskId,
      });
      children.forEach((child) => {
        // Don't update trashed tasks unless we're un-deleting
        if (child.trashedAt && mergedUpdates.trashedAt !== null) return;

        const childUpdate: OrgTaskUpdates = {};
        if (mergedUpdates.assignedTo !== undefined)
          if (child.assignedTo === oldTask.assignedTo)
            // Only update the children if it is the same as the parent
            childUpdate.assignedTo = mergedUpdates.assignedTo;

        // If both are unset, or both were completed at the same time and recently (i.e. untick causes an undo for children)
        const bufferForUndo = 10 * 60 * 60 * 1000; // 10 minutes
        if (mergedUpdates.completedAt !== undefined) {
          // Unset children if they have the same completedAt time as the parent
          // and was set recently
          if (
            mergedUpdates.completedAt === null &&
            oldTask.completedAt &&
            child.completedAt === oldTask.completedAt &&
            oldTask.completedAt + bufferForUndo > Date.now()
          ) {
            childUpdate.completedAt = null;
          }
          // Only all child to be updated if the child completedAt is not set
          if (mergedUpdates.completedAt && !child.completedAt) {
            childUpdate.completedAt = mergedUpdates.completedAt;
          }
        }

        // Move children into currentAt
        if (mergedUpdates.currentAt && !child.currentAt) {
          childUpdate.currentAt = mergedUpdates.currentAt;
        }

        // Move children out of currentAt
        if (mergedUpdates.currentAt === null && child.currentAt) {
          childUpdate.currentAt = null;
        }

        // Trash child
        if (mergedUpdates.trashedAt && !child.trashedAt) {
          childUpdate.trashedAt = mergedUpdates.trashedAt;
        }

        // Un-trash child
        if (mergedUpdates.trashedAt === null && child.trashedAt) {
          childUpdate.trashedAt = null;
        }

        // Add tags
        if (mergedUpdates.push?.tagIds !== undefined) {
          const pushChildTagIds = mergedUpdates.push?.tagIds?.filter(
            (tagId) => !child.tagIds.includes(tagId)
          );
          if (pushChildTagIds?.length)
            childUpdate.push = { tagIds: pushChildTagIds };
        }
        // Remove tags
        if (mergedUpdates.pull?.tagIds !== undefined) {
          const pullChildTagIds = mergedUpdates.pull?.tagIds?.filter((tagId) =>
            child.tagIds.includes(tagId)
          );
          if (pullChildTagIds?.length)
            childUpdate.pull = { tagIds: pullChildTagIds };
        }

        if (Object.keys(childUpdate).length > 0) {
          this.updateTask({ orgId, spaceId, taskId: child._id }, childUpdate);
        } else {
          // If there is no update just re-send the child so that lists can see
          // if this child should be added to a list as a result of a change to
          // the parent
          this.taskUpdatesSubject.next([child]);
        }
      });
    }

    // If we aren't updating the children, just inform the lists that they may
    // need to include this child in a top-level list
    if (!applyUpdateToChildren) {
      const children = this.getChildrenMutable({
        orgId,
        spaceId,
        parentId: taskId,
      });
      this.taskUpdatesSubject.next(children);
    }

    // Send updates to parent
    // Complete parent
    const parentId = updatedTask.parentId;
    if (mergedUpdates.completedAt && parentId) {
      const siblings = this.getChildrenMutable({
        orgId,
        spaceId,
        parentId,
      }).filter((c) => !c.trashedAt);

      const incompleteSiblings = siblings.filter(
        (c) => !c.completedAt && c._id !== taskId
      );

      if (incompleteSiblings.length === 0)
        this.updateTask(
          { orgId, spaceId, taskId: parentId },
          { completedAt: mergedUpdates.completedAt },
          { preventChildPropagation: true } // don't send down to child tasks (i.e. this task)
        );
    }
    // Un-complete parent
    if (mergedUpdates.completedAt === null && parentId) {
      this.updateTask(
        { orgId, spaceId, taskId: parentId },
        { completedAt: null },
        { preventChildPropagation: true } // don't send down to child tasks (i.e. this task)
      );
    }

    if (isNewTask) {
      // POST/CREATE
      let { title, description } = mergedUpdates;
      // Only create new tasks if the title or description is set
      if (title || description) {
        title = title ?? '';
        if (typeof description !== 'string') description = ''; // default in the case of new task

        if (!serverTask.salt) throw new Error('Expected salt to be set');

        const newTaskTagIds = _.uniq([
          ...existingTask.tagIds,
          ...(mergedUpdates.push?.tagIds ?? []),
        ]);

        const body: TaskPost.ReqBody = {
          spaceId,
          salt: serverTask.salt,
          tagIds: newTaskTagIds,
          assignedTo: mergedUpdates.assignedTo ?? updatedTask.assignedTo,
          currentAt: mergedUpdates.currentAt ?? updatedTask.currentAt,
          trashedAt: mergedUpdates.trashedAt ?? updatedTask.trashedAt,
          weight: mergedUpdates.weight ?? updatedTask.weight,
          parentId: mergedUpdates.parentId ?? updatedTask.parentId,
          parentWeight: mergedUpdates.parentWeight ?? updatedTask.parentWeight,
          title,
          description,
          createdBy: updatedTask.createdBy,
          localAt: updatedTask.localAt,
          files: [],
        };

        if (body.assignedTo === zeroId) body.assignedTo = undefined;

        const path = generatePath<TaskPost.ReqParams>(
          '/api/orgs/:orgId/tasks/:taskId',
          { orgId, taskId }
        );

        this.http.post<TaskPost.ResBody>(path, body).subscribe({
          next: (t) => {
            this.storeServerTaskData({ task: t });
          },
          error: (err) => {
            if (err.status === 0)
              this.addUpdateToBacklog({ orgId, spaceId, _id: taskId });
            else console.error(err);
          },
        });
      }
    } else {
      // PATCH/UPDATE
      const body: TaskPatch.ReqBody = JSON.parse(JSON.stringify(mergedUpdates)); // Remove undefined fields.

      const updatedFields = Object.keys(body);
      if (updatedFields.length === 0) return;

      const path = generatePath<TaskPatch.ReqParams>(
        '/api/orgs/:orgId/tasks/:taskId',
        { orgId, taskId }
      );

      this.http.patch<TaskPatch.ResBody>(path, body).subscribe({
        next: (t) => {
          this.storeServerTaskData({ task: t });
        },
        error: (err) => {
          if (err.status === 0) {
            this.addUpdateToBacklog(updatedTask);
          }
        },
      });
    }
  }

  updateTitleWait({
    orgId,
    spaceId,
    taskId,
    title,
  }: {
    orgId: string;
    spaceId: string;
    taskId: string;
    title: string;
  }) {
    const spaceData = this.getSpaceDataForStoring({ orgId, spaceId });
    const task = spaceData.taskMap.get(taskId);
    if (!task) throw new Error('task not found');

    if (task.serverTask.title === title) {
      task.title = title;
      if (task.updates) task.updates.title = undefined;
      return;
    }

    if (!task.updates) task.updates = {};
    task.updates.title = title;
    task.title = title;

    this.taskUpdatesSubject.next([task]);
    this.startSaveTitleInFuture(task);
  }

  private startSaveTitleInFuture(task: OrgTaskWithUpdates) {
    if (!task.subscriptions.saveTitleDebounce) {
      task.subscriptions.saveTitleDebounce = new Subject();
      task.subscriptions.saveTitleDebounceSub =
        task.subscriptions.saveTitleDebounce
          .pipe(debounceTime(5000))
          .subscribe(() => {
            const { orgId, spaceId, _id: taskId } = task;
            this.forceSave({ orgId, spaceId, taskId });
            if (task.subscriptions.saveTitleDebounceSub)
              task.subscriptions.saveTitleDebounceSub.unsubscribe();
            task.subscriptions.saveTitleDebounce = undefined;
            task.subscriptions.saveTitleDebounceSub = undefined;
          });
    }

    task.subscriptions.saveTitleDebounce.next(true);
  }

  updateDescriptionWait({
    orgId,
    spaceId,
    taskId,
    description,
  }: {
    orgId: string;
    spaceId: string;
    taskId: string;
    description: string;
  }) {
    const spaceData = this.getSpaceDataForStoring({ orgId, spaceId });
    const task = spaceData.taskMap.get(taskId);
    if (!task) throw new Error('task not found');

    if (task.serverTask.description === description) {
      task.description = description;
      if (task.updates) task.updates.description = undefined;
      return;
    }

    if (!task.updates) task.updates = {};
    task.updates.description = description;
    task.description = description;

    this.taskUpdatesSubject.next([task]);
    this.startSaveDescriptionInFuture(task);
  }

  private startSaveDescriptionInFuture(task: OrgTaskWithUpdates) {
    if (!task.subscriptions.saveDescriptionDebounce) {
      task.subscriptions.saveDescriptionDebounce = new Subject();
      task.subscriptions.saveDescriptionDebounceSub =
        task.subscriptions.saveDescriptionDebounce
          .pipe(debounceTime(5000))
          .subscribe(() => {
            const { orgId, spaceId, _id: taskId } = task;
            this.forceSave({ orgId, spaceId, taskId });
            if (task.subscriptions.saveDescriptionDebounceSub)
              task.subscriptions.saveDescriptionDebounceSub.unsubscribe();
            task.subscriptions.saveDescriptionDebounce = undefined;
            task.subscriptions.saveDescriptionDebounceSub = undefined;
          });
    }

    task.subscriptions.saveDescriptionDebounce.next(true);
  }

  forceSave({
    orgId,
    spaceId,
    taskId,
  }: {
    orgId: string;
    spaceId: string;
    taskId: string;
  }) {
    this.updateTask({ orgId, spaceId, taskId }, {});
  }

  removeIfBlankAndNotSaved({
    orgId,
    spaceId,
    taskId,
  }: {
    orgId: string;
    spaceId: string;
    taskId: string;
  }): boolean {
    const spaceData = this.getSpaceDataForStoring({ orgId, spaceId });
    const task = spaceData.taskMap.get(taskId);

    if (!task) return false; // possibly already removed // #indexCheck-removeIfBlankAndNotSaved

    if (!task.autoDeletable) return false;

    // Check that the task is not created yet. If it is created, don't remove
    if (task.serverTask.createdAt) return false; // #isNewTask when createdAt === 0

    // Check that there is no title/description
    if (task.updates?.title) return false;
    if (task.updates?.description) return false;

    // Check that there are no children (we only need to find one then we should bail)
    const children = this.getSpaceDataForStoring({
      orgId,
      spaceId,
    }).parentTaskMap.get(taskId);
    if (children?.length) return false;

    this.removeTaskAndMapStructures({ orgId, spaceId, taskId });
    return true;
  }

  removeTaskAndMapStructures({
    orgId,
    spaceId,
    taskId,
  }: {
    orgId: string;
    spaceId: string;
    taskId: string;
  }) {
    const spaceData = this.getSpaceDataForStoring({ orgId, spaceId });
    const task = spaceData.taskMap.get(taskId);
    if (!task) return; // possibly already removed
    spaceData.taskMap.delete(taskId);
    this.taskRemoveSubject.next({ orgId, spaceId, taskId });

    task.subscriptions.saveTitleDebounceSub?.unsubscribe();
    task.subscriptions.saveDescriptionDebounceSub?.unsubscribe();

    spaceData.taskMap.delete(taskId);
    spaceData.parentTaskMap.delete(taskId);

    const parentId = task.parentId;
    if (parentId) {
      const parentChildren = spaceData.parentTaskMap.get(parentId);
      if (parentChildren) {
        const childIndex = parentChildren.findIndex((c) => c._id === taskId);
        if (childIndex !== -1) parentChildren.splice(childIndex, 1);
      }
    }
  }

  addUpdateToBacklog({
    orgId,
    spaceId,
    _id: taskId,
  }: {
    orgId: string;
    spaceId: string;
    _id: string;
  }) {
    const spaceData = this.getSpaceDataForStoring({ orgId, spaceId });

    if (!spaceData.serverBacklogTasks.find(({ _id }) => _id === taskId)) {
      spaceData.serverBacklogTasks.push({ _id: taskId });
    }
  }

  removeUpdateFromBacklog({
    orgId,
    spaceId,
    _id: taskId,
  }: {
    orgId: string;
    spaceId: string;
    _id: string;
  }) {
    const spaceData = this.getSpaceDataForStoring({ orgId, spaceId });

    const index = spaceData.serverBacklogTasks.findIndex(
      ({ _id }) => _id === taskId
    );
    if (index !== -1) spaceData.serverBacklogTasks.splice(index, 1);
  }

  createTask({
    taskId,
    salt,
    orgId,
    spaceId,
    createdBy,
    title = '',
    description,
    tagIds,
    assignedTo,
    timeEstimate,
    schedule,
    dueAt,
    currentAt,
    completedAt,
    weight,
    parentId,
    parentWeight,
    autoDeletable,
  }: {
    taskId?: string;
    salt?: string;
    orgId: string;
    spaceId: string;
    createdBy: string;
    title?: string;
    description?: string;
    tagIds: string[];
    assignedTo?: string;
    timeEstimate?: number;
    schedule?: Schedule;
    dueAt?: number;
    currentAt?: number;
    completedAt?: number;
    weight: number;
    parentId?: string;
    parentWeight?: number;
    autoDeletable?: true;
  }) {
    if (salt && !taskId) throw new Error('salt provided but no taskId');
    if (taskId && !salt) throw new Error('taskId provided but no salt');

    if (!salt || !taskId) ({ salt, _id: taskId } = generateIdFromOrgId(orgId));

    // Create generic task
    const task: OrgTaskFrontEnd = {
      _id: taskId,
      orgId,
      spaceId,
      tagIds,
      assignedTo,
      weight: 0,
      title: '',
      description: '',

      files: [],
      userIds: [],
      userUpdates: [],
      messageNumber: 0,
      createdBy,
      localAt: Date.now(),
      createdAt: 0, // Not created (new task) #isNewTask when createdAt === 0
      updatedAt: 0, // Not updated (new task)
    };

    const serverTask: OrgTaskFrontEnd & { salt?: string } = {
      ..._.cloneDeep(task),
      salt,
    };

    const orgTaskForStorage: OrgTaskWithUpdates = {
      ..._.cloneDeep(task),
      serverTask,
      autoDeletable, // allows task to be deleted when clicking off
      subscriptions: {},
    };

    const spaceData = this.getSpaceDataForStoring({ orgId, spaceId });
    spaceData.taskMap.set(taskId, orgTaskForStorage);
    this.storeTaskMap(orgTaskForStorage);

    const updates: OrgTaskUpdates = {
      push: tagIds ? { tagIds: _.cloneDeep(tagIds) } : undefined,
      assignedTo,
      timeEstimate,
      schedule,
      dueAt,
      currentAt,
      completedAt,
      weight,
      parentId,
      parentWeight,
      title,
      description,
    };

    // Immediately save task
    this.updateTask({ orgId, spaceId, taskId }, updates);

    return orgTaskForStorage;
  }

  permanentlyDeleteTask(task: DeepReadonly<OrgTaskWithUpdates>) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = { task };
    const dialogRef = this.dialog.open(DeleteTaskDialogComponent, dialogConfig);
    let dialogSubscription: Subscription | undefined = undefined;
    dialogSubscription = dialogRef.afterClosed().subscribe({
      next: (res) => {
        if (!res) return;

        const { orgId, _id: taskId, title } = task;
        const path = generatePath<TaskDelete.ReqParams>(
          '/api/orgs/:orgId/tasks/:taskId',
          { orgId, taskId }
        );
        this.http.delete<TaskDelete.ResBody>(path).subscribe({
          next: (tasksResult) => {
            const tasks: OrgTaskWithUpdates[] = [];
            tasksResult.forEach((t) => {
              const stored = this.storeServerTaskData({ task: t });
              if (stored) tasks.push(stored);
            });

            this.taskUpdatesSubject.next(tasks);

            this.snackBar.open('Deleted: ' + title, 'Dismiss', {
              duration: 5000,
            });
          },
          error: () => {
            this.snackBar.open(
              "Error while deleting '" + title + "'",
              'Dismiss',
              {
                duration: 5000,
              }
            );
          },
        });

        dialogSubscription?.unsubscribe();
      },
      error: (err) => {
        console.error(err);
        dialogSubscription?.unsubscribe();
      },
    });
  }

  getTaskLocalSync({
    orgId,
    spaceId,
    taskId,
  }: {
    orgId: string;
    spaceId?: string; // spaceId is optional, increases speed when looking up entries locally
    taskId: string;
  }): OrgTaskWithUpdates | undefined {
    let value: OrgTaskWithUpdates | undefined = undefined;
    if (spaceId) {
      value = this.getSpaceDataForStoring({ orgId, spaceId }).taskMap.get(
        taskId
      );
    } else {
      // Check all spaces because we don't know the spaceId
      this.spaceData
        .filter((s) => s.orgId === orgId)
        .forEach((s) => {
          const possibleValue = s.taskMap.get(taskId);
          if (possibleValue) value = possibleValue;
        });
    }

    return value;
  }

  getTaskAsync({
    orgId,
    spaceId,
    taskId,
  }: {
    orgId: string;
    spaceId?: string; // spaceId is optional, increases speed when looking up entries locally
    taskId: string;
  }) {
    const returnSubject = new Subject<DeepReadonly<OrgTaskWithUpdates>>();
    const localTask = this.getTaskLocalSync({ orgId, spaceId, taskId });
    if (localTask) {
      setTimeout(() => returnSubject.next(localTask), 1);
    } else {
      const path = generatePath<TaskGet.ReqParams>(
        '/api/orgs/:orgId/tasks/:taskId',
        { orgId, taskId }
      );
      this.http.get<TaskGet.ResBody>(path).subscribe({
        next: ({ task }) => {
          const taskFromStore = this.storeServerTaskData({ task });
          if (taskFromStore) {
            returnSubject.next(taskFromStore);
            return;
          }
          // Task may have been updated already, try local again
          const localTask2 = this.getTaskLocalSync({ orgId, spaceId, taskId });
          if (localTask2) returnSubject.next(localTask2);
          else returnSubject.error('task not found');
        },
        error: (err) => returnSubject.error(err),
      });
    }
    return returnSubject;
  }

  getStoredTasks({
    spaceId,
    listFilter,
  }: {
    spaceId: string;
    listFilter: ListFilter;
  }): DeepReadonly<OrgTaskWithUpdates>[] {
    const spaceData = this.spaceData.find((d) => d.spaceId === spaceId);
    if (!spaceData)
      throw new Error('spaceId not found when attempting to getStoredTasks');

    const tasks = [...spaceData.taskMap]
      .map(([taskId, task]) => task)
      .filter((task) => taskMatch(task, listFilter));
    return tasks;
  }

  searchTasks({
    query,
    orgId,
    spaceId,
    listContext,
  }: {
    query: string;
    orgId: string;
    spaceId: string;
    listContext: { listId: string; listFilter: ListFilter }[];
  }) {
    const subject = new ReplaySubject<SearchResult[]>(1);

    const spaceData = this.spaceData.find((d) => d.spaceId === spaceId);
    if (!spaceData) {
      subject.error('spaceId not found when attempting to getStoredTasks');
      return subject.asObservable();
    }

    const fragments = query
      .split(' ')
      .filter((f) => f.length)
      .map((f) => f.toLowerCase());

    const results: SearchResult[] = [...spaceData.taskMap]
      .map(([taskId, task]) => task)
      .filter((task) => {
        let matching = true;
        fragments.forEach((f) => {
          if (!(task.title + ' ' + task.description).toLowerCase().includes(f))
            matching = false;
        });
        return matching;
      })
      .map((task) => {
        const taskId = task._id;
        const minIndex = Math.min(
          ...fragments.map((f) =>
            (task.title + ' ' + task.description).toLowerCase().indexOf(f)
          )
        );
        const weight = 1 / (minIndex + 1);
        const parents = this.getParents({ orgId, spaceId, taskId });

        let contextFound = false;
        const parentPathTaskIds: string[] = [];

        let contextData:
          | {
              inCurrentContext: true;
              listId: string;
              parentPathTaskIds: string[];
            }
          | {
              inCurrentContext: false;
              listId?: undefined;
              parentPathTaskIds?: undefined;
            } = { inCurrentContext: false };

        listContext.forEach((list) => {
          const p = [...parents, task];
          while (p.length && !contextFound) {
            const current = p.pop();
            if (current) {
              parentPathTaskIds.unshift(current._id);
              if (!contextFound) {
                contextFound = taskMatch(current, list.listFilter);
                if (contextFound) {
                  contextData = {
                    inCurrentContext: true,
                    listId: list.listId,
                    parentPathTaskIds,
                  };
                }
              }
            }
          }
        });

        return {
          orgId,
          spaceId,
          taskId,
          title: task.title,
          completed: !!task.completedAt,
          weight,
          ...contextData,
        };
      });

    results.sort(sortByTitleExactFirst(query));

    subject.next(results);
    subject.complete();

    return subject.asObservable();
  }

  private getChildrenMutable({
    orgId,
    spaceId,
    parentId,
  }: {
    orgId: string;
    spaceId: string;
    parentId: string;
  }): OrgTaskWithUpdates[] {
    const children =
      this.getSpaceDataForStoring({
        orgId,
        spaceId,
      }).parentTaskMap.get(parentId) ?? [];

    children.sort(sortTaskByParentWeight);

    return children;
  }

  getChildren({
    orgId,
    spaceId,
    parentId,
  }: {
    orgId: string;
    spaceId: string;
    parentId: string;
  }): DeepReadonly<OrgTaskWithUpdates>[] {
    return this.getChildrenMutable({ orgId, spaceId, parentId });
  }

  getParents({
    orgId,
    spaceId,
    taskId,
  }: {
    orgId: string;
    spaceId: string;
    taskId: string;
  }): DeepReadonly<OrgTaskWithUpdates>[] {
    const spaceData = this.spaceData.find((d) => d.spaceId === spaceId);
    if (!spaceData) throw new Error('spaceId not found in getParents');

    const task = this.getTaskLocalSync({ orgId, spaceId, taskId });
    if (!task) {
      console.error('task not found in getParents');
      return [];
    }

    const parentTasks: DeepReadonly<OrgTaskWithUpdates>[] = [];
    let parentId = task.parentId;
    const parentTaskIds = parentId ? [parentId] : [];
    let duplicate = false; // To prevent infinite loops (where children are parents of parents)
    while (parentId && !duplicate) {
      const nextParent = this.getTaskLocalSync({
        orgId,
        spaceId,
        taskId: parentId,
      });
      if (nextParent) {
        parentId = nextParent.parentId;
        if (parentId && parentTaskIds.includes(parentId)) duplicate = true;
        parentTasks.unshift(nextParent);
        if (parentId) parentTaskIds.unshift(parentId);
      } else {
        console.error('parent not found in getParents');
        parentId = undefined;
      }
    }

    return parentTasks;
  }

  getOldestCompletedTask({ spaceId }: { spaceId: string }): number {
    const spaceData = this.spaceData.find((d) => d.spaceId === spaceId);
    if (!spaceData)
      throw new Error('spaceId not found when attempting to getOldestTask');
    let completedAt = Date.now();
    [...spaceData.taskMap]
      .map(([taskId, task]) => task)
      .forEach((d) => {
        if (d.completedAt && d.completedAt < completedAt)
          completedAt = d.completedAt;
      });

    return completedAt;
  }

  /**
   * Used to update the tasks that are either in or out of a list
   */
  spaceTaskUpdateFeed({ spaceId }: { spaceId: string }) {
    return this.taskUpdatesSubject.pipe(
      map((u) => u.filter((t) => t.spaceId === spaceId)),
      filter((u) => u.length > 0)
    );
  }

  childTaskUpdateFeed({ parentId }: { parentId: string }) {
    return this.taskUpdatesSubject.pipe(
      map((u) => u.filter((t) => t.parentId === parentId)),
      filter((u) => u.length > 0)
    );
  }

  taskUpdates({ taskId }: { taskId: string }) {
    return this.taskUpdatesSubject.pipe(
      map((u) => u.find((t) => t._id === taskId)),
      filter((u) => u !== undefined) as OperatorFunction<
        OrgTaskWithUpdates | undefined,
        OrgTaskWithUpdates
      >
    );
  }

  taskRemoves({
    orgId,
    spaceId,
    taskId,
  }: {
    orgId: string;
    spaceId: string;
    taskId?: string;
  }) {
    return this.taskRemoveSubject.pipe(
      filter(
        (t) =>
          t.orgId === orgId &&
          t.spaceId === spaceId &&
          (!taskId || t.spaceId === spaceId)
      )
    );
  }

  downloadTasks({ spaceId }: { spaceId: string }) {
    const spaceData = this.spaceData.find((d) => d.spaceId === spaceId);
    if (!spaceData) throw new Error('spaceId not found');

    const { orgId } = spaceData;
    const time = new Date().toJSON().slice(0, 19);
    const tasksWithServerTasks = _.cloneDeep(
      [...spaceData.taskMap].map(([taskId, task]) => task)
    );
    const tasks = tasksWithServerTasks.map((t) => {
      const { serverTask, updates, ...u } = t;
      return u;
    });
    const tags = _.cloneDeep(
      this.tagService.getAllTagsSync({ orgId, spaceId })
    );

    const userOrgObj = this.authUserService.getAuthUserOrgObj({ orgId });
    const space = userOrgObj.org.spaces.find((s) => s._id === spaceId);
    if (!space) throw new Error('spaceId not found');

    const taskExportDownload: TaskExportDownload = {
      type: 'fociva-export v0.0.2',
      orgId: spaceData.orgId,
      spaceId,
      users: userOrgObj.org.users.map(({ _id, name }) => ({ _id, name })),
      tasks,
      tags,
    };
    const json = JSON.stringify(taskExportDownload);
    const ele = document.createElement('a');
    ele.setAttribute(
      'href',
      'data:text/json;charset=UTF-8,' + encodeURIComponent(json)
    );
    ele.setAttribute('download', 'tasks-' + time + '.json');
    ele.style.display = 'none';
    document.body.appendChild(ele);
    ele.click();
    document.body.removeChild(ele);
  }

  importTasks({ orgId, spaceId }: { orgId: string; spaceId: string }) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = { spaceId, orgId };
    const importTasksDialogRef = this.dialog.open(
      ImportTasksComponent,
      dialogConfig
    );

    const sub = importTasksDialogRef.afterClosed().subscribe({
      next: (res) => {
        // Use res if needed
        sub?.unsubscribe();
      },
      error: (err) => {
        sub?.unsubscribe();
      },
    });
  }

  bulkCreate({
    orgId,
    spaceId,
    tasks,
  }: {
    orgId: string;
    spaceId: string;
    tasks: ImportBulkTask[];
  }) {
    const returnSubject = new Subject<boolean>();

    const path = generatePath<TaskPostBatch.ReqParams>(
      '/api/orgs/:orgId/spaces/:spaceId/tasks',
      { orgId, spaceId }
    );
    const body: TaskPostBatch.ReqBody = tasks;
    this.http.post<TaskPostBatch.ResBody>(path, body).subscribe({
      next: ({ tasks: taskResults }) => {
        const updatesForSubject: OrgTaskWithUpdates[] = [];
        taskResults.forEach((task) => {
          const taskFromStore = this.storeServerTaskData({
            task,
            updateSubject: false,
          });
          if (!taskFromStore) return; // this task is already out of date
          updatesForSubject.push(taskFromStore);
        });
        this.taskUpdatesSubject.next(updatesForSubject);
        returnSubject.next(true);
      },
      error: (err) => {
        returnSubject.error(err);
      },
    });

    return returnSubject.asObservable();
  }

  /**
   * Default is to select at the end of the task textarea
   */
  setTaskCursor({
    taskId,
    listId,
    start = 10000,
    end: inputEnd,
  }: {
    taskId: string;
    listId: string;
    start?: number;
    end?: number;
  }) {
    const end = inputEnd === undefined ? start : inputEnd;
    this.taskCursorSubject.next({ taskId, listId, start, end });
  }

  taskCursor({ taskId, listId }: { taskId: string; listId: string }) {
    return this.taskCursorSubject.pipe(
      filter((f) => f.taskId === taskId && f.listId === listId)
    );
  }

  getSelectionStart() {
    if (!this.selectionStart) return undefined;
    const { taskId, listId } = this.selectionStart;
    return { taskId, listId };
  }

  setTaskSelectionStart(input: SelectionData) {
    const originalSelections = this.selections;

    this.selectionStart = input;
    this.selectionEnd = input;
    this.selections = [input];
    this.selectionsSubject.next(this.selections);

    // Update selections
    originalSelections.forEach((s) => this.deselectSubject.next(s));
    this.selectSubject.next(input);
    this.selectDisplaySubject.next(input);
  }

  removeTaskSelection({
    listId,
    taskId,
    parentId,
    orphaned,
  }: {
    listId: string;
    taskId: string;
    parentId: string | undefined;
    orphaned: boolean;
  }) {
    if (
      this.selectionStart &&
      this.selectionStart.taskId === taskId &&
      this.selectionStart.listId === listId &&
      this.selectionStart.parentId === parentId &&
      this.selectionStart.orphaned === orphaned
    ) {
      this.selectionStart = undefined;
    }
    if (
      this.selectionEnd &&
      this.selectionEnd.taskId === taskId &&
      this.selectionEnd.listId === listId &&
      this.selectionEnd.parentId === parentId &&
      this.selectionEnd.orphaned === orphaned
    ) {
      this.selectionEnd = undefined;
    }

    const existingIndex = this.selections.findIndex(
      (s) =>
        taskId === s.taskId &&
        listId === s.listId &&
        parentId === s.parentId &&
        orphaned === s.orphaned
    );
    if (existingIndex !== -1) {
      this.selections.splice(existingIndex, 1);
      this.selectionsSubject.next(this.selections);
    }

    this.deselectSubject.next({ taskId, listId, parentId, orphaned });
  }

  controlToggleSelection(selection: SelectionData) {
    const existingIndex = this.selections.findIndex(
      (s) =>
        selection.taskId === s.taskId &&
        selection.listId === s.listId &&
        selection.parentId === s.parentId &&
        selection.orphaned === s.orphaned
    );

    if (existingIndex !== -1) {
      // Deselect
      this.selections.splice(existingIndex, 1);
      this.selectionsSubject.next(this.selections);
      if (
        this.selectionStart &&
        this.selectionStart.taskId === selection.taskId &&
        this.selectionStart.listId === selection.listId &&
        this.selectionStart.parentId === selection.parentId &&
        this.selectionStart.orphaned === selection.orphaned
      )
        this.selectionStart = undefined;
      if (
        this.selectionEnd &&
        this.selectionEnd.taskId === selection.taskId &&
        this.selectionEnd.listId === selection.listId &&
        this.selectionEnd.parentId === selection.parentId &&
        this.selectionEnd.orphaned === selection.orphaned
      )
        this.selectionEnd = this.selectionStart;
      this.deselectSubject.next({
        taskId: selection.taskId,
        listId: selection.listId,
        parentId: selection.parentId,
        orphaned: selection.orphaned,
      });
    } else if (
      this.selections.length > 0 &&
      !this.selections.map((s) => s.listId).includes(selection.listId)
    ) {
      const originalSelections = this.selections;
      this.selections = [selection];
      originalSelections.forEach(({ listId, taskId, parentId, orphaned }) =>
        this.deselectSubject.next({ listId, taskId, parentId, orphaned })
      );
      this.selections.forEach(({ listId, taskId, parentId, orphaned }) =>
        this.selectSubject.next({ listId, taskId, parentId, orphaned })
      );
    } else {
      // Select
      this.selections.push(selection);
      this.selectionsSubject.next(this.selections);
      if (!this.selectionStart) this.selectionStart = selection;
      this.selectionEnd = selection;
      this.selectSubject.next({
        taskId: selection.taskId,
        listId: selection.listId,
        parentId: selection.parentId,
        orphaned: selection.orphaned,
      });
      this.selectDisplaySubject.next({
        listId: selection.listId,
        taskId: undefined,
      });
      this.blurSelected();
    }
  }

  shiftSetSelections(selections: SelectionData[], selectionEnd: SelectionData) {
    const originalSelections = this.selections;

    this.selections = selections;
    this.selectionsSubject.next(this.selections);
    this.selectionEnd = selectionEnd;

    // We're forcing selections using this method, so all get removed
    originalSelections.forEach(({ listId, taskId, parentId, orphaned }) =>
      this.deselectSubject.next({ listId, taskId, parentId, orphaned })
    );
    this.selections.forEach(({ listId, taskId, parentId, orphaned }) =>
      this.selectSubject.next({ listId, taskId, parentId, orphaned })
    );
    const listIds = _.uniq(this.selections.map((s) => s.listId));
    if (listIds.length)
      this.selectDisplaySubject.next({
        listId: listIds[0],
        taskId: undefined,
      });
    this.blurSelected();
  }

  blurSelected() {
    if (this.selections.length > 1)
      this.selections.forEach((s) => this.blurSubject.next(s));
  }

  selectObservable({
    listId,
    taskId,
    parentId,
    orphaned,
  }: {
    listId: string;
    taskId: string;
    parentId: string | undefined;
    orphaned: boolean;
  }) {
    return this.selectSubject.pipe(
      filter(
        (s) =>
          s.taskId === taskId &&
          s.listId === listId &&
          s.parentId === parentId &&
          s.orphaned === orphaned
      ),
      map((s) => true)
    );
  }

  selectDisplayObservable() {
    return this.selectDisplaySubject.asObservable();
  }

  deselectObservable({
    listId,
    taskId,
    parentId,
    orphaned,
  }: {
    listId: string;
    taskId: string;
    parentId: string | undefined;
    orphaned: boolean;
  }) {
    return this.deselectSubject.pipe(
      filter(
        (s) =>
          s.taskId === taskId &&
          s.listId === listId &&
          s.parentId === parentId &&
          s.orphaned === orphaned
      ),
      map((s) => true)
    );
  }

  blurObservable({ listId, taskId }: { listId: string; taskId: string }) {
    return this.blurSubject.pipe(
      filter((s) => s.taskId === taskId && s.listId === listId),
      map((s) => true)
    );
  }

  tabInSelectionObservable({
    listId,
    taskId,
  }: {
    listId: string;
    taskId: string;
  }) {
    return this.tabInSelectionSubject.pipe(
      filter((s) => s.taskId === taskId && s.listId === listId),
      map((s) => true)
    );
  }

  /**
   * Sends a message to the parent task of the selected tasks to get its info
   * and the task after as they will be the new siblings.
   */
  tabOutSelectionObservable({
    listId,
    taskId,
  }: {
    listId: string;
    taskId: string;
  }) {
    return this.tabOutSelectionSubject.pipe(
      filter((s) => s.taskId === taskId && s.listId === listId),
      map((s) => true)
    );
  }

  selectionValidateCanTab() {
    const { selections } = this;
    if (selections.length === 0) return;

    const orgIds = _.uniq(selections.map((s) => s.orgId));
    if (orgIds.length > 1) {
      // Deselect all (this is an error)
      selections.forEach((s) => this.removeTaskSelection(s));
      return;
    }
    const orgId = orgIds[0];

    const spaceIds = _.uniq(selections.map((s) => s.spaceId));
    if (spaceIds.length !== 1) {
      // Deselect all (this is an error)
      selections.forEach((s) => this.removeTaskSelection(s));
      return;
    }
    const spaceId = spaceIds[0];

    const listIds = _.uniq(selections.map((s) => s.listId));
    if (listIds.length !== 1) {
      this.snackBar.open('Cannot tab tasks across multiple levels', 'Dismiss', {
        duration: 5000,
      });
      return;
    }
    const listId = listIds[0];

    const parentIds = _.uniq(selections.map((s) => s.parentId));
    if (parentIds.length !== 1) {
      this.snackBar.open('Cannot tab tasks across multiple levels', 'Dismiss', {
        duration: 5000,
      });
      return;
    }
    const parentId = parentIds[0];

    const orphanFound = selections.find((s) => s.orphaned);
    if (orphanFound) return;

    return {
      orgId,
      spaceId,
      parentId,
      listId,
    };
  }

  selectionCannotTabIn() {
    // Tab in attempt but no prior task that can be a parent.
    this.snackBar.open(
      'Cannot tab in without a suitable parent above these tasks',
      'Dismiss',
      { duration: 5000 }
    );
  }

  selectionTabIn({ parentId }: { parentId: string }) {
    // Always put at the end of the list
    const result = this.selectionValidateCanTab();
    if (!result) return;
    const { orgId, spaceId } = result;

    const children = this.getChildrenMutable({ orgId, spaceId, parentId });
    const lastChild = children[children.length - 1];

    let start = lastChild?.parentWeight ?? placeMidWeight(undefined, undefined);

    const tasks = this.getWeightSortedSelectedTasks();

    // Update the parentId to indicate the new parent
    // #ParentResetExplanation
    // This must happen before the updates occur, because otherwise the tabbing
    // will cause the selected task to disappear from a list, causing it to
    // automatically become deselected.
    this.selections.forEach((s) => (s.parentId = parentId));

    // Place tasks one after the other
    tasks.forEach((t) => {
      const parentWeight = start + incrementalTaskWeight;
      this.updateTask(
        { orgId, spaceId, taskId: t._id },
        { parentId, parentWeight }
      );
      start = parentWeight;
    });

    return;
  }

  selectedTabOutToParent({
    parentId,
    parentWeight: { start, end },
  }: {
    parentId: string;
    parentWeight: { start: number; end: number | undefined };
  }) {
    const result = this.selectionValidateCanTab();
    if (!result) return;
    const { orgId, spaceId, parentId: selectionsParent } = result;
    if (!selectionsParent) return; // Can't tab out in this situation
    const count = this.selections.length;
    const spacing = end
      ? Math.floor((end - start) / (count + 1))
      : incrementalTaskWeight;
    const tasks = this.getParentWeightSortedSelectedTasks();
    // see #ParentResetExplanation
    this.selections.forEach((s) => (s.parentId = parentId));
    tasks.forEach((t) => {
      const parentWeight = start + spacing;
      this.updateTask(
        { orgId, spaceId, taskId: t._id },
        { parentId, parentWeight }
      );
      start = parentWeight;
    });
  }

  selectedTabOutNoParent({
    weight: { start, end },
  }: {
    weight: { start: number; end: number | undefined };
  }) {
    const result = this.selectionValidateCanTab();
    if (!result) return;
    const { orgId, spaceId, parentId: selectionsParent } = result;
    if (!selectionsParent) return; // Can't tab out in this situation
    const count = this.selections.length;
    const spacing = end
      ? Math.floor((end - start) / (count + 1))
      : incrementalTaskWeight;
    const tasks = this.getParentWeightSortedSelectedTasks();
    // see #ParentResetExplanation
    this.selections.forEach((s) => (s.parentId = undefined));
    tasks.forEach((t) => {
      const weight = start + spacing;
      this.updateTask(
        { orgId, spaceId, taskId: t._id },
        { parentId: null, weight }
      );
      start = weight;
    });
  }

  arrowObservable({ listId, taskId }: { listId: string; taskId: string }) {
    return this.arrowSubject.pipe(
      filter((s) => s.taskId === taskId && s.listId === listId)
    );
  }

  subListExpandObservable({
    listId,
    taskId,
  }: {
    listId: string;
    taskId: string;
  }) {
    return this.subListExpandSubject.pipe(
      filter((s) => s.taskId === taskId && s.listId === listId)
    );
  }

  launchDialogObservable({
    listId,
    taskId,
  }: {
    listId: string;
    taskId: string;
  }) {
    return this.launchDialogSubject.pipe(
      filter((s) => s.taskId === taskId && s.listId === listId)
    );
  }

  getParentWeightSortedSelectedTasks() {
    const tasks: OrgTaskWithUpdates[] = [];
    this.selections.forEach(({ orgId, spaceId, taskId }) => {
      const task = this.getTaskLocalSync({ orgId, spaceId, taskId });
      if (task) tasks.push(task);
    });
    tasks.sort((a, b) => {
      if (
        typeof a.parentWeight === 'number' &&
        typeof b.parentWeight === 'number'
      )
        return a.parentWeight - b.parentWeight;
      return 0;
    });
    return tasks;
  }

  getWeightSortedSelectedTasks() {
    const tasks: OrgTaskWithUpdates[] = [];
    this.selections.forEach(({ orgId, spaceId, taskId }) => {
      const task = this.getTaskLocalSync({ orgId, spaceId, taskId });
      if (task) tasks.push(task);
    });
    tasks.sort((a, b) => a.weight - b.weight);
    return tasks;
  }

  checkIfSelected({
    listId,
    taskId,
    parentId,
    orphaned,
  }: {
    listId: string;
    taskId: string;
    parentId: string | undefined;
    orphaned: boolean;
  }) {
    return !!this.selections.find(
      (s) =>
        s.taskId === taskId &&
        s.listId === listId &&
        s.parentId === parentId &&
        s.orphaned === orphaned
    );
  }

  applyListFilter({
    draggedTask,
    listFilter,
    time,
    draggedTaskFromSameList,
    draggedTaskContextDepth,
  }: {
    draggedTask: DeepReadonly<OrgTaskWithUpdates>;
    listFilter: DeepReadonly<ListFilter>;
    time: number;
    draggedTaskFromSameList: boolean;
    draggedTaskContextDepth: number;
  }) {
    // Make sure that the task has all the relevant fields to fulfill listData
    const assignedTo = listFilter.assignedTo;

    let currentAt: number | null | undefined = undefined;
    let completedAt: number | null | undefined = undefined;
    let trashedAt: number | null | undefined = undefined;

    if (listFilter.listStage === 'backlog') {
      if (draggedTask.currentAt) currentAt = null;
      if (draggedTask.completedAt) completedAt = null;
      if (draggedTask.trashedAt) trashedAt = null;
    }

    if (listFilter.listStage === 'current') {
      if (!draggedTask.currentAt) currentAt = time;
      if (draggedTask.completedAt) completedAt = null;
      if (draggedTask.trashedAt) trashedAt = null;
    }

    if (listFilter.listStage === 'completed') {
      if (!draggedTask.completedAt) completedAt = time;
      if (!draggedTask.trashedAt) trashedAt = null;
    }

    if (listFilter.listStage === 'trash') trashedAt = time;

    const tagIds = listFilter.tagIds?.map((t) => t);

    const parentId =
      draggedTaskFromSameList && draggedTaskContextDepth !== 0
        ? null
        : undefined;

    return {
      parentId,
      assignedTo,
      currentAt,
      completedAt,
      trashedAt,
      push: { tagIds },
    };
  }

  openSchedule(input: { orgId: string; spaceId: string; taskId: string }) {
    const dialogConfig = new MatDialogConfig();
    const data: TaskScheduleDialogComponentData = {
      ...input,
    };
    dialogConfig.data = data;
    this.dialog.open(TaskScheduleDialogComponent, dialogConfig);
  }

  changeCompletedAt(input: { orgId: string; spaceId: string; taskId: string }) {
    const dialogConfig = new MatDialogConfig();
    const data: ChangeDateDialogComponentData = {
      ...input,
      type: 'completedAt',
    };
    dialogConfig.data = data;
    this.dialog.open(ChangeDateDialogComponent, dialogConfig);
  }

  changeCurrentAt(input: { orgId: string; spaceId: string; taskId: string }) {
    const dialogConfig = new MatDialogConfig();
    const data: ChangeDateDialogComponentData = {
      ...input,
      type: 'currentAt',
    };
    dialogConfig.data = data;
    this.dialog.open(ChangeDateDialogComponent, dialogConfig);
  }

  scrollTaskIntoView({ orgId, taskId }: { orgId: string; taskId: string }) {
    // Get task async to most likely get all the tasks
    let sub: Subscription | undefined = undefined;
    sub = this.getTaskAsync({ orgId, taskId }).subscribe({
      next: (task) => {
        const parents = this.getParents({
          orgId,
          spaceId: task.spaceId,
          taskId,
        });
        const parentPathTaskIds = parents.map((p) => p._id);
        parentPathTaskIds.unshift(taskId);
        this.taskScrollSubject.next({ orgId, taskId, parentPathTaskIds });
        if (sub) sub.unsubscribe();
      },
      error: () => {
        if (sub) sub.unsubscribe();
      },
    });
  }

  getScrollObservable({ taskId }: { taskId: string }) {
    return this.taskScrollSubject.pipe(
      filter((s) => !!s && s.parentPathTaskIds.includes(taskId)),
      map((s) => ({ isTargetTask: !!s && s.taskId === taskId }))
    );
  }

  cancelScrollTaskIntoView() {
    this.taskScrollSubject.next(undefined);
  }

  stopAutoDelete({
    orgId,
    spaceId,
    taskId,
  }: {
    orgId: string;
    spaceId: string;
    taskId: string;
  }) {
    const task = this.getSpaceDataForStoring({ orgId, spaceId }).taskMap.get(
      taskId
    );
    if (!task) throw new Error('taskId not found');
    task.autoDeletable = undefined;
    this.taskUpdatesSubject.next([task]);
  }
}

export function taskMatch(
  task: DeepReadonly<OrgTaskWithUpdates>,
  listFilter: ListFilter
) {
  const { listStage, tagIds, assignedTo, completedAtRange } = listFilter;

  const recentlyCompletedBuffer =
    listFilter.listStage === 'backlog' || listFilter.listStage === 'current'
      ? listFilter.recentlyCompletedBuffer
      : undefined;

  // Filter assigned
  if (assignedTo === null) {
    if (task.assignedTo) return false;
  } else if (assignedTo) {
    if (task.assignedTo !== assignedTo) return false;
  }

  if (tagIds === null && task.tagIds.length !== 0) return false;
  // Filter tags (all tagIds must be present)
  if (tagIds && !tagIds.every((tagId) => task.tagIds.includes(tagId)))
    return false;

  // Filter current/deleted or not
  if (
    listStage === 'backlog' &&
    (typeof task.currentAt === 'number' || typeof task.trashedAt === 'number')
  )
    return false;

  if (
    listStage === 'current' &&
    (typeof task.currentAt !== 'number' || typeof task.trashedAt === 'number')
  )
    return false;

  if (
    listStage === 'trash' &&
    (typeof task.trashedAt !== 'number' || typeof task.deletedAt === 'number')
  )
    return false;

  // Filter completed
  if (
    (listStage === 'backlog' || listStage === 'current') &&
    task.completedAt &&
    (!recentlyCompletedBuffer || task.completedAt < recentlyCompletedBuffer)
  ) {
    return false;
  }

  // Filter completed by date
  if (listStage === 'completed') {
    // Completed tasks must all have completedAt set
    if (typeof task.completedAt !== 'number') return false;
    // Check in specified range
    if (completedAtRange) {
      if (
        typeof completedAtRange.end === 'number' &&
        completedAtRange.end <= task.completedAt
      ) {
        return false;
      }
      if (task.completedAt < completedAtRange.start) {
        return false;
      }
    }
  }

  return true;
}

export function sortTaskByParentWeight(
  a: OrgTaskWithUpdates,
  b: OrgTaskWithUpdates
) {
  if (typeof a.parentWeight === 'number' && typeof b.parentWeight === 'number')
    return a.parentWeight - b.parentWeight;
  return 0;
}

export function sortTaskItemByParentWeight(a: TaskItem, b: TaskItem) {
  if (
    typeof a.task.parentWeight === 'number' &&
    typeof b.task.parentWeight === 'number'
  )
    return a.task.parentWeight - b.task.parentWeight;
  return 0;
}

function applyUpdatesToTask({
  serverTask,
  updates: inputUpdates,
}: {
  serverTask: OrgTaskFrontEnd;
  updates: OrgTaskUpdates | undefined;
}): OrgTaskFrontEnd {
  const task = _.cloneDeep(serverTask);
  const updates = _.cloneDeep(inputUpdates);

  if (!updates) return task;

  const {
    assignedTo,
    weight,
    parentId,
    parentWeight,
    timeEstimate,
    schedule,
    dueAt,
    currentAt,
    completedAt,
    title,
    description,
  } = updates;

  if (assignedTo) task.assignedTo = assignedTo;
  if (assignedTo === null) task.assignedTo = undefined;
  if (typeof weight === 'number') task.weight = weight;
  if (parentId) task.parentId = parentId;
  if (parentWeight) task.parentWeight = parentWeight;
  if (parentId === null) {
    task.parentId = undefined;
    task.parentWeight = undefined;
  }
  if (typeof timeEstimate === 'number') task.timeEstimate = timeEstimate;
  if (timeEstimate === null) task.timeEstimate = undefined;
  if (schedule) task.schedule = schedule;
  if (schedule === null) task.schedule = undefined;
  if (typeof dueAt === 'number') task.dueAt = dueAt;
  if (dueAt === null) task.dueAt = undefined;
  if (typeof currentAt === 'number') task.currentAt = currentAt;
  if (currentAt === null) task.currentAt = undefined;
  if (typeof completedAt === 'number') task.completedAt = completedAt;
  if (completedAt === null) task.completedAt = undefined;
  if (typeof title === 'string') task.title = title;
  if (typeof description === 'string') task.description = description;

  const pushTagIds = updates.push?.tagIds?.filter(
    (t) => !task.tagIds.includes(t)
  );
  pushTagIds?.forEach((t) => task.tagIds.push(t));

  updates.pull?.tagIds?.forEach((t) => {
    const index = task.tagIds.indexOf(t);
    if (index !== -1) task.tagIds.splice(index, 1);
  });

  updates.pull?.fileIds?.forEach((fileId) => {
    const index = task.files.findIndex((file) => fileId === file._id);
    if (index !== -1) task.files.splice(index, 1);
  });

  const pushUserIds = updates.push?.userIds?.filter(
    (t) => !task.userIds.includes(t)
  );
  pushUserIds?.forEach((t) => task.userIds.push(t));

  return task;
}
